import React from 'react';
import { Profile } from '@/types/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Shield } from 'lucide-react';

interface ProfileHeaderWidgetProps {
  profile: Profile;
}

export const ProfileHeaderWidget: React.FC<ProfileHeaderWidgetProps> = ({ profile }) => {
  const backgroundImage = profile.profile_image_url;
  
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Background Image */}
      {backgroundImage ? (
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5" />
      )}
      
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
      
      {/* Content overlay */}
      <div className="relative z-10 h-full p-4 flex flex-col justify-end">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-white truncate">
              {profile.display_name || profile.username}
            </h1>
            {profile.is_admin && (
              <Sparkles className="h-4 w-4 text-yellow-400 flex-shrink-0" />
            )}
            {profile.is_premium && (
              <Shield className="h-4 w-4 text-purple-400 flex-shrink-0" />
            )}
          </div>
          
          <p className="text-sm text-white/80">@{profile.username}</p>
          
          <div className="flex gap-1 flex-wrap">
            {profile.is_admin && (
              <Badge variant="secondary" className="text-xs bg-black/40 text-white border-white/20">
                Admin
              </Badge>
            )}
            {profile.is_premium && (
              <Badge variant="outline" className="text-xs border-purple-400 text-purple-300 bg-black/40">
                Premium
              </Badge>
            )}
            {profile.registration_number && (
              <Badge variant="outline" className="text-xs border-white/40 text-white bg-black/40">
                #{profile.registration_number}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};